import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Check, Loader2, Car, RefreshCw } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const RoleCard = ({ role, day, index, onSelect }) => {
  const isDriver = /driver/i.test(role.role);
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.05, ease }}
      data-testid={`role-card-${role.role}`}
      className="rounded-2xl border border-[#8E1226]/12 bg-[#FBF7F0] p-7 md:p-8 transition-colors duration-300 hover:border-[#0E6B3A]/40"
    >
      <div className="flex items-start gap-3">
        <h4 className="font-serif text-2xl md:text-[1.7rem] leading-tight tracking-tight text-[#8E1226]">
          {role.role}
        </h4>
        {isDriver && (
          <Car className="w-5 h-5 text-[#6D726A] shrink-0 mt-1.5" strokeWidth={1.5} />
        )}
      </div>
      <p className="mt-3 text-sm text-[#6D726A] leading-relaxed">
        {role.description}
      </p>
      <div className="mt-6 flex flex-col gap-2.5">
        {role.slots.map((slot) => {
          const full = slot.remaining <= 0;
          return (
            <button
              key={slot.id}
              data-testid={`slot-select-${slot.id}`}
              disabled={full}
              onClick={() => onSelect({ ...slot, role: role.role, day_label: day.label })}
              className={`flex items-center justify-between rounded-xl border px-5 py-4 text-lg transition-all duration-300 ${
                full
                  ? "border-[#8E1226]/10 text-[#6D726A] cursor-not-allowed bg-[#F2E8D5]/40"
                  : "border-[#8E1226]/15 text-[#8E1226] hover:border-[#0E6B3A] hover:bg-[#0E6B3A] hover:text-[#FBF7F0] group"
              }`}
            >
              <span className="font-semibold tracking-tight">
                {slot.start} – {slot.end}
              </span>
              <span
                className={`text-base tracking-wide ${
                  full ? "" : "text-[#6D726A] group-hover:text-[#FBF7F0]/80"
                }`}
              >
                {full ? "Fully booked" : `${slot.remaining} of ${slot.capacity} left`}
              </span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

export const SignupSection = ({ c }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [active, setActive] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [manageOpen, setManageOpen] = useState(false);
  const [manageEmail, setManageEmail] = useState("");
  const [manageSubmitting, setManageSubmitting] = useState(false);

  const requestManageLink = async (e) => {
    e.preventDefault();
    setManageSubmitting(true);
    try {
      await api.post("/manage/request-link", { email: manageEmail });
      toast.success(
        "If we found a booking for that email, we've sent you a link to manage it."
      );
      setManageOpen(false);
      setManageEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setManageSubmitting(false);
    }
  };

  const loadShifts = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/shifts");
      setDays(data.days || []);
      setActive((prev) => prev || data.days?.[0]?.id);
    } catch (e) {
      setError(true);
      toast.error("Could not load volunteer shifts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShifts();
  }, []);

  const openFor = (slot) => {
    setSelected(slot);
    setDone(false);
    setForm({ name: "", email: "", phone: "", notes: "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post("/signups", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        shift_id: selected.id,
      });
      setDone(true);
      toast.success("You're signed up! A confirmation email is on its way.");
      loadShifts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="signup"
      data-testid="signup-section"
      className="bg-[#F2E8D5] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14 md:mb-20">
          <span className="text-xs uppercase tracking-[0.32em] font-semibold text-[#0E6B3A]">
            {c?.signup_eyebrow || "Choose your shift"}
          </span>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#8E1226]">
            {c?.signup_title || "Pick a day, a role, a time."}
          </h2>
          <p className="mt-6 text-[#6D726A] text-base md:text-lg leading-relaxed">
            {c?.signup_paragraph}
          </p>
          <button
            data-testid="manage-link-open-btn"
            onClick={() => setManageOpen(true)}
            className="mt-6 text-sm text-[#8E1226] underline underline-offset-4 decoration-[#0E6B3A] hover:text-[#0E6B3A] transition-colors"
          >
            Already signed up? Change or cancel your shift →
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#6D726A]">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading shifts…
          </div>
        ) : error ? (
          <div
            data-testid="signup-error"
            className="rounded-2xl border border-[#8E1226]/12 bg-[#FBF7F0] p-10 text-center max-w-xl"
          >
            <p className="text-[#8E1226] text-lg font-serif">
              We couldn't load the volunteer shifts just now.
            </p>
            <p className="mt-2 text-[#6D726A] text-sm">
              This is usually a brief hiccup. Please try again.
            </p>
            <button
              data-testid="signup-retry-btn"
              onClick={loadShifts}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#8E1226] text-[#FBF7F0] px-7 py-3 text-sm font-medium hover:bg-[#0E6B3A] transition-colors"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.7} /> Try again
            </button>
          </div>
        ) : days.length === 0 ? (
          <div
            data-testid="signup-empty"
            className="rounded-2xl border border-[#8E1226]/12 bg-[#FBF7F0] p-10 text-center max-w-xl text-[#6D726A]"
          >
            No volunteer shifts have been published yet. Please check back soon.
          </div>
        ) : (
          <Tabs value={active} onValueChange={setActive} className="w-full">
            <TabsList
              data-testid="day-tabs"
              className="bg-transparent p-0 h-auto gap-3 mb-12 flex-wrap justify-start"
            >
              {days.map((d) => (
                <TabsTrigger
                  key={d.id}
                  value={d.id}
                  data-testid={`day-tab-${d.id}`}
                  className="rounded-full border border-[#8E1226]/15 px-8 py-5 text-xl font-bold data-[state=active]:bg-[#8E1226] data-[state=active]:text-[#FBF7F0] data-[state=inactive]:text-[#8E1226] transition-colors"
                >
                  {d.label}
                  <span className="ml-2 font-normal opacity-70">
                    {d.weekday} {d.date.slice(8)} Dec
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            <p
              data-testid="signup-instruction"
              className="mb-10 -mt-6 text-lg font-semibold text-[#8E1226]"
            >
              {c?.signup_instruction}
            </p>

            {days.map((d) => (
              <TabsContent key={d.id} value={d.id} className="mt-0">
                <p className="mb-10 font-serif italic text-2xl md:text-3xl text-[#8E1226]/80 max-w-2xl">
                  {d.blurb}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 items-start">
                  {d.roles.map((role, i) => (
                    <RoleCard
                      key={role.role}
                      role={role}
                      day={d}
                      index={i}
                      onSelect={openFor}
                    />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="signup-dialog"
          className="bg-[#FBF7F0] border-[#8E1226]/10 sm:max-w-[480px]"
        >
          {done ? (
            <div className="py-6 text-center" data-testid="signup-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#8E1226] flex items-center justify-center mb-6">
                <Check className="w-7 h-7 text-[#FBF7F0]" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-3xl text-[#8E1226]">Thank you!</h3>
              <p className="mt-3 text-[#6D726A] leading-relaxed">
                You're signed up for{" "}
                <strong className="text-[#8E1226]">{selected?.role}</strong> on{" "}
                {selected?.day_label} ({selected?.start} – {selected?.end}). A
                confirmation email is on its way — we'll be in touch with the
                details.
              </p>
              <p
                data-testid="signup-onday-note"
                className="mt-4 rounded-xl bg-[#F2E8D5] px-5 py-4 text-sm text-[#8E1226] leading-relaxed text-left"
              >
                <strong>On the day:</strong> please sign in at the office first —{" "}
                {c?.contact_address} — where we'll give you your guidance for the
                day.
              </p>
              <button
                data-testid="signup-close-btn"
                onClick={() => setOpen(false)}
                className="mt-7 rounded-full bg-[#8E1226] text-[#FBF7F0] px-7 py-3 text-sm font-medium hover:bg-[#0E6B3A] transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif font-normal text-3xl text-[#8E1226] tracking-tight">
                  {selected?.role}
                </DialogTitle>
                <DialogDescription className="text-[#6D726A]">
                  {selected?.day_label} · {selected?.start} – {selected?.end}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 mt-2">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-[#8E1226] text-sm">
                    Full name
                  </Label>
                  <Input
                    id="name"
                    data-testid="signup-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
                    placeholder="Mary O'Sullivan"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[#8E1226] text-sm">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="signup-email-input"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
                    placeholder="you@example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-[#8E1226] text-sm">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    data-testid="signup-phone-input"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
                    placeholder="08X XXX XXXX"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="notes" className="text-[#8E1226] text-sm">
                    Anything we should know?{" "}
                    <span className="text-[#6D726A]">(optional)</span>
                  </Label>
                  <Textarea
                    id="notes"
                    data-testid="signup-notes-input"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="bg-transparent border border-[#8E1226]/20 rounded-lg focus-visible:ring-0 focus-visible:border-[#0E6B3A] resize-none"
                    rows={3}
                    placeholder="Dietary needs, accessibility, bringing a friend…"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="signup-submit-btn"
                  disabled={submitting}
                  className="w-full rounded-full bg-[#0E6B3A] text-[#FBF7F0] py-5 text-lg font-bold tracking-wide hover:bg-[#083D21] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                  {submitting ? "Signing you up…" : "Click here to sign me up"}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent
          data-testid="manage-link-dialog"
          className="bg-[#FBF7F0] border-[#8E1226]/10 sm:max-w-[440px]"
        >
          <DialogHeader>
            <DialogTitle className="font-serif font-normal text-3xl text-[#8E1226] tracking-tight">
              Manage your shift
            </DialogTitle>
            <DialogDescription className="text-[#6D726A]">
              Enter the email you signed up with and we'll send you a secure
              link to change or cancel your shift.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={requestManageLink} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="manage-email" className="text-[#8E1226] text-sm">
                Email
              </Label>
              <Input
                id="manage-email"
                type="email"
                data-testid="manage-email-input"
                required
                value={manageEmail}
                onChange={(e) => setManageEmail(e.target.value)}
                className="bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              data-testid="manage-link-submit-btn"
              disabled={manageSubmitting}
              className="w-full rounded-full bg-[#8E1226] text-[#FBF7F0] py-3.5 text-sm font-medium tracking-wide hover:bg-[#0E6B3A] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {manageSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Send me the link
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
